/**
 * Member Portal Authentication Library
 * Handles API calls for member login, registration, and authentication
 */

import { exitImpersonation, handleImpersonationResponse, isImpersonating } from '@/lib/impersonation';

// Environment-aware API URL
// Always use full URLs in production for Tauri desktop app compatibility
// This works for both Vercel web (direct API call) and Tauri (no proxy available)
const getApiBaseUrl = () => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    const environment = import.meta.env.VITE_ENVIRONMENT;

    // Log for debugging
    console.log('🔍 memberAuth - Environment:', environment, '| VITE_WEAVEIN_API_URL:', import.meta.env.VITE_WEAVEIN_API_URL);

    // On dev.weavein.ai, always use relative URLs (Vercel proxy rewrites handle routing)
    if (typeof window !== 'undefined' && window.location.hostname === 'dev.weavein.ai') {
      return '/api/member-portal';
    }
    // Use env var with ?? to preserve empty string (Vercel proxy uses relative URLs)
    const baseUrl = import.meta.env.VITE_WEAVEIN_API_URL ?? 'https://weavein-api.onrender.com';
    return `${baseUrl.trim()}/api/member-portal`;
  }
  // Fallback
  return '/api/member-portal';
};

const API_BASE_URL = getApiBaseUrl();

// Log the final API URL
console.log('🌐 memberAuth - API_BASE_URL:', API_BASE_URL);

export interface InvitationData {
  valid: boolean;
  email: string;
  first_name: string;
  last_name: string;
  organization: string;
}

export interface RegisterMemberData {
  invitationToken: string;
  password: string;
  phoneNumber?: string;
  preferredContactMethod?: 'email' | 'sms' | 'phone' | 'portal';
}

export interface MemberOrganization {
  cbo_id: number;
  organization_name: string;
  status: string;
  is_primary?: boolean;
  is_current?: boolean;
}

export interface MemberLoginResponse {
  token: string;
  member: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
    preferred_contact_method?: string;
    organization_id: number;
    organization_name: string;
  };
  organizations?: MemberOrganization[];
  dashboard: {
    active_referrals: number;
    completed_referrals: number;
    unread_messages: number;
    upcoming_appointments: number;
  };
}

export interface MemberDashboard {
  total_referrals: number;
  active_referrals: number;
  completed_referrals: number;
  total_enrollments: number;
  active_enrollments: number;
  received_communications: number;
  unread_communications: number;
  pending_responses: number;
  total_documents: number;
}

export interface Communication {
  id: number;
  communication_type: string;
  subject?: string;
  message_body: string;
  status: string;
  requires_response: boolean;
  created_at: string;
  sent_at?: string;
  read_at?: string;
  responded_at?: string;
  response_message?: string;
  sent_by_name?: string;
  sender_name?: string;
  program_name?: string;
  is_bulk_communication?: boolean;
  is_read?: boolean;
  priority?: string;
  // Workspace message fields
  source?: string;  // 'workspace' or 'member_communications'
  channel_id?: number;
  channel_name?: string;
  message_id?: number;
  unread?: boolean;
  // Unified timeline fields
  direction?: 'inbound' | 'outbound';
  campaign_id?: number;
}

// Unified message for cross-channel timeline
export interface UnifiedMessage {
  id: number;
  source_type: string;  // 'member_communication', 'bulk_message', 'workspace', 'campaign_response'
  source_id: number;
  member_id: number;
  direction: 'inbound' | 'outbound';
  message_type: string;  // 'sms', 'email', 'portal', 'workspace', 'phone'
  subject?: string;
  content_preview: string;
  status: string;
  is_read: boolean;
  sender_name?: string;
  sent_at?: string;
  created_at: string;
  channel_id?: number;
  campaign_id?: number;
}

export interface UnifiedTimeline {
  timeline: UnifiedMessage[];
  total: number;
  channels: {
    type: string;
    last_message_at?: string;
    message_count: number;
  }[];
}

export interface Note {
  id: number;
  note_type: string;
  subject?: string;
  note_text: string;
  is_private: boolean;
  created_at: string;
  updated_at: string;
  referral_id?: number;
  enrollment_id?: number;
}

export interface CreateNoteRequest {
  noteType: string;
  subject?: string;
  noteText: string;
  isPrivate: boolean;
  referralId?: number;
  enrollmentId?: number;
}

export interface Document {
  id: number;
  document_name: string;
  document_type: string;
  mime_type: string;
  file_size_bytes: number;
  file_path: string;
  description?: string;
  is_verified: boolean;
  created_at: string;
  referral_id?: number;
  enrollment_id?: number;
}

export interface PortalSettings {
  emailNotificationsEnabled: boolean;
  smsNotificationsEnabled: boolean;
  preferredContactMethod: string;
  allowPortalMessages: boolean;
}

/**
 * Verify an invitation token
 */
export async function verifyInvitation(token: string): Promise<InvitationData> {
  try {
    const response = await fetch(`${API_BASE_URL}/invitations/verify/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Invalid or expired invitation' }));
      throw new Error(error.detail || 'Invalid or expired invitation');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Failed to verify invitation:', error);
    throw new Error('Failed to verify invitation. Please check your connection and try again.');
  }
}

/**
 * Register a new member - Step 1: Create account and send SMS
 */
export async function registerMemberStep1(data: RegisterMemberData): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/register/step1`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Registration failed' }));
    throw new Error(error.detail || 'Registration failed');
  }

  return response.json();
}

/**
 * Register - Step 2: Verify phone number
 */
export async function registerVerifyPhone(email: string, code: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/register/verify-phone`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Verification failed' }));
    throw new Error(error.detail || 'Verification failed');
  }

  return response.json();
}

/**
 * Register - Update phone number and resend verification code
 * Used when user enters wrong phone number and can't receive SMS
 */
export async function registerUpdatePhone(email: string, phoneNumber: string): Promise<{
  success: boolean;
  message: string;
  phone_number_masked: string;
}> {
  const response = await fetch(`${API_BASE_URL}/register/update-phone`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, phone_number: phoneNumber }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update phone number' }));
    throw new Error(error.detail || 'Failed to update phone number');
  }

  return response.json();
}

/**
 * Register - Resend verification code to current phone number
 * Used when code expires or wasn't received
 */
export async function registerResendCode(email: string): Promise<{
  success: boolean;
  message: string;
  phone_number_masked: string;
}> {
  const response = await fetch(`${API_BASE_URL}/register/resend-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to resend code' }));
    throw new Error(error.detail || 'Failed to resend code');
  }

  return response.json();
}

/**
 * Register - Step 3: Sign consent documents
 */
export async function registerSignConsent(
  email: string,
  termsSignature: string,
  hipaaSignature: string
): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/register/sign-consent`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      terms_signature: termsSignature,
      hipaa_signature: hipaaSignature,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Signing consent failed' }));
    throw new Error(error.detail || 'Signing consent failed');
  }

  return response.json();
}

/**
 * Store a member session from any sign-in response (invitation registration,
 * email + password, a text code, or self sign-up). One place, so every way in
 * leaves the browser in the same state. No-op when the response has no token.
 */
export function storeMemberSession(result: any): void {
  if (!result?.token) return;
  localStorage.setItem('member_token', result.token);
  localStorage.setItem('member_data', JSON.stringify(result.member));
  // Persist the refresh token so apiClient can silently renew the access
  // token instead of firing a global auth:logout when it expires (the shared
  // shell fires capability/feature calls through apiClient for members too).
  if (result.refresh_token) {
    localStorage.setItem('refresh_token', result.refresh_token);
  }
  // Seed the active org (X-CBO-ID) so member endpoints scope to the login org.
  if (result.member?.organization_id) {
    localStorage.setItem('active_cbo_id', String(result.member.organization_id));
  }
  if (result.organizations) {
    localStorage.setItem('member_organizations', JSON.stringify(result.organizations));
  }
}

/**
 * Register - Step 4: Complete registration and get JWT
 */
export async function registerComplete(email: string): Promise<MemberLoginResponse> {
  const response = await fetch(`${API_BASE_URL}/register/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Completion failed' }));
    throw new Error(error.detail || 'Completion failed');
  }

  const result = await response.json();

  // Store token in localStorage
  storeMemberSession(result);

  return result;
}

/**
 * Get active consent documents
 */
export async function getActiveConsentDocuments(): Promise<any[]> {
  const consentUrl = API_BASE_URL.replace('/api/member-portal', '/api/consent/documents/active');
  const response = await fetch(consentUrl, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error('Failed to load consent documents');
  }

  return response.json();
}

/**
 * Login a member - Step 1: Send credentials and get 2FA code
 */
export async function loginMemberStep1(email: string, password: string): Promise<any> {
  const response = await fetch(`${API_BASE_URL}/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Invalid email or password' }));
    throw new Error(error.detail || 'Invalid email or password');
  }

  const result = await response.json();

  // 2FA-skip path (admin-exempt or 2FA disabled at user/org level): step1
  // returns a full session token directly, with no requires_2fa flag. Persist
  // it exactly like the verify-2fa path so the caller can navigate straight to
  // the dashboard instead of silently no-opping on the login screen.
  storeMemberSession(result);

  return result;
}

/**
 * Login a member - Step 2: Verify 2FA code
 */
export async function loginMemberVerify2FA(email: string, code: string): Promise<MemberLoginResponse> {
  const response = await fetch(`${API_BASE_URL}/login/verify-2fa`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, code }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Invalid verification code' }));
    throw new Error(error.detail || 'Invalid verification code');
  }

  const result = await response.json();

  // Store token in localStorage
  storeMemberSession(result);

  return result;
}

/**
 * Login a member (legacy - for backward compatibility)
 */
export async function loginMember(email: string, password: string): Promise<MemberLoginResponse> {
  // This function now just calls step1 - you should use the 2-step process instead
  const step1Result = await loginMemberStep1(email, password);

  // Return partial result indicating 2FA is required
  return {
    ...step1Result,
    requires_2fa: true
  } as any;
}

/**
 * Logout member
 */
export function logoutMember(): void {
  // TRUS-747: signing out while a super admin views as this member ends that
  // session and returns to the admin; it never touches the member's devices.
  if (isImpersonating()) {
    void exitImpersonation('logout');
    return;
  }
  // Stop browser pushes to this computer (TRUS-668). The token is captured now:
  // it is removed below, before the request leaves.
  const token = localStorage.getItem('member_token');
  // The "Turn on notifications" banner shows again at the next sign-in.
  import('@/lib/webPush')
    .then((m) => {
      m.resetWebPushBannerDismissal();
      m.resetWebPushFailures();
      if (token) return m.disableWebPush(m.memberTransport(token));
    })
    .catch(() => {});
  localStorage.removeItem('member_token');
  localStorage.removeItem('member_data');
  localStorage.removeItem('member_organizations');
}

/**
 * Get current member token
 */
export function getMemberToken(): string | null {
  return localStorage.getItem('member_token');
}

/**
 * Get current member data
 */
export function getMemberData(): any | null {
  try {
    const data = localStorage.getItem('member_data');
    if (!data) return null;
    return JSON.parse(data);
  } catch (error) {
    console.error('Error parsing member data from localStorage:', error);
    // Clear corrupted data
    localStorage.removeItem('member_data');
    return null;
  }
}

/**
 * Check if member is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getMemberToken();
}

/**
 * Make authenticated API request
 */
export async function authenticatedFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = getMemberToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  return withImpersonationCheck(
    fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    }),
    endpoint,
  );
}

/**
 * Make an authenticated member API request that ALSO sends the active-org
 * (X-CBO-ID) header. Member-portal endpoints scope every read/write to the
 * member's active organization, so this header is required on all of them.
 * Mirrors authenticatedFetch but adds X-CBO-ID from localStorage.
 */
export async function memberApiFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
  const token = getMemberToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'X-CBO-ID': localStorage.getItem('active_cbo_id') || '',
    ...options.headers,
  };

  // Most member endpoints live under /api/member-portal (API_BASE_URL). A few
  // shared, user-scoped endpoints (e.g. /api/notifications) sit at the API root
  // — members authenticate as users, so those work with the member token +
  // X-CBO-ID header. When an endpoint is already app-absolute (starts with
  // "/api/"), target the API root instead of the member-portal sub-base.
  return withImpersonationCheck(fetch(memberApiUrl(endpoint), { ...options, headers }), endpoint);
}

/**
 * TRUS-747: while a super admin views as this member, a 401 means the session
 * ended (back to the admin) and a 403 may be the View as user policy (shown in
 * the banner). The response is returned unchanged either way.
 */
async function withImpersonationCheck(pending: Promise<Response>, endpoint: string): Promise<Response> {
  const res = await pending;
  if (isImpersonating() && (res.status === 401 || res.status === 403)) {
    res
      .clone()
      .json()
      .then((body) => handleImpersonationResponse(res.status, body, endpoint))
      .catch(() => {});
  }
  return res;
}

/** Absolute URL for a member API call (see memberApiFetch). */
export function memberApiUrl(endpoint: string): string {
  return endpoint.startsWith('/api/')
    ? `${API_BASE_URL.replace('/api/member-portal', '')}${endpoint}`
    : `${API_BASE_URL}${endpoint}`;
}

// ---------------------------------------------------------------------------
// Notifications (shared staff/user notification system, member-authed)
// The /api/notifications routes are user-scoped (members log in as users), so
// they work with the member token + X-CBO-ID. Mirrors the staff NotificationBell
// / NotificationCenter data model but uses member auth (memberApiFetch), never
// the staff apiClient.
// ---------------------------------------------------------------------------
export interface MemberNotification {
  id: number;
  user_id: number;
  organization_id: number | null;
  notification_type: string;
  title: string;
  message: string;
  link: string | null;
  icon: string | null;
  priority: string;
  read: boolean;
  read_at: string | null;
  archived: boolean;
  archived_at: string | null;
  related_member_id: number | null;
  related_workflow_id: number | null;
  related_task_id: number | null;
  related_referral_id: number | null;
  related_enrollment_id: number | null;
  related_activity_id: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface MemberNotificationList {
  notifications: MemberNotification[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface MemberNotificationSummary {
  unread_count: number;
  read_count: number;
  urgent_unread_count: number;
  total_count: number;
  last_notification_at: string | null;
  recent_unread: MemberNotification[];
}

export interface GetMemberNotificationsOpts {
  page?: number;
  page_size?: number;
  unread_only?: boolean;
}

/** Paginated notifications for the logged-in member (scoped to active org). */
export async function getMemberNotifications(
  opts: GetMemberNotificationsOpts = {},
): Promise<MemberNotificationList> {
  const params = new URLSearchParams();
  params.set('page', String(opts.page ?? 1));
  params.set('page_size', String(opts.page_size ?? 50));
  if (opts.unread_only) params.set('unread_only', 'true');
  const res = await memberApiFetch(`/api/notifications?${params.toString()}`);
  if (!res.ok) throw new Error('Failed to load notifications');
  return res.json();
}

/** Unread-count summary + recent unread items — poll this for the bell badge. */
export async function getMemberNotificationSummary(): Promise<MemberNotificationSummary> {
  const res = await memberApiFetch('/api/notifications/summary');
  if (!res.ok) throw new Error('Failed to load notification summary');
  return res.json();
}

/** Mark a single notification read. */
export async function markMemberNotificationRead(id: number): Promise<void> {
  const res = await memberApiFetch(`/api/notifications/${id}/read`, { method: 'PATCH' });
  if (!res.ok) throw new Error('Failed to mark notification as read');
}

/** Mark every unread notification read for the current member. */
export async function markAllMemberNotificationsRead(): Promise<void> {
  const res = await memberApiFetch('/api/notifications/mark-all-read', { method: 'PATCH' });
  if (!res.ok) throw new Error('Failed to mark all notifications as read');
}

/**
 * Map a notification `link` (staff/SPA path) to a member-portal route.
 * Returns a `/member/...` path when the link maps to a page in the portal, the
 * link unchanged when it's already a `/member` path, or null when it points at
 * a staff-only route (caller should NOT navigate off the portal in that case).
 */
export function mapNotificationLinkToMemberRoute(link: string | null): string | null {
  if (!link) return null;
  let path = link;
  try {
    const u = new URL(link, window.location.origin);
    path = u.pathname + u.search;
  } catch {
    /* already a relative path */
  }
  if (path.startsWith('/member')) return path;
  const clean = path.replace(/^\//, '');
  const [base] = clean.split('?');
  const seg = base.split('/')[0];
  const MEMBER_ROUTES = new Set([
    'dashboard',
    'inbox',
    'referrals',
    'documents',
    'notes',
    'settings',
    'enrollments',
    'incentives',
    'work-compliance',
    'courses',
    'training',
    'notifications',
  ]);
  if (MEMBER_ROUTES.has(seg)) return `/member/${base}`;
  return null;
}

// ---------------------------------------------------------------------------
// Integrated Inbox (member-portal/inbox/*) — one conversation surface across
// member_communications ("comm") and workspace channels ("channel").
// thread_key contains a colon (e.g. "comm:123") → URL-encode it in the path.
// ---------------------------------------------------------------------------
/** How a conversation / message is happening — surfaced as a badge in the inbox. */
export type InboxChannel = 'SMS' | 'Email' | 'Portal' | 'Phone' | 'Workspace';

export interface InboxThread {
  thread_key: string;
  source: 'comm' | 'channel';
  channel?: InboxChannel | null;
  title: string;
  counterparty: string;
  preview: string;
  unread_count: number;
  requires_response: boolean;
  is_starred: boolean;
  is_flagged: boolean;
  updated_at: string | null;
  /**
   * TRUS-709: an old `comm:` thread whose messages now live in the member DM
   * names that DM here ("channel:<id>"); the inbox shows the DM only.
   */
  redirect_thread_key?: string | null;
}

export interface InboxThreadsResponse {
  threads: InboxThread[];
  unread_total: number;
}

export interface InboxMessage {
  id: number;
  direction: 'inbound' | 'outbound';
  /** TRUS-702: portal | sms | email | call | voicemail | note | chat | system (older servers: capitalised names). */
  channel?: InboxChannel | string | null;
  /** TRUS-702: the old display label ("SMS", "Workspace") moved here. */
  channel_label?: string | null;
  body: string;
  sender_name: string;
  created_at: string | null;
  /** Channel-message metadata when the backend passes it through (TRUS-668: metadata.call). */
  metadata?: Record<string, any> | null;
  /**
   * TRUS-638 read state (channel threads only; absent on `comm:` threads).
   * On the member's own message: read by any staff / how many / earliest read.
   * On a staff message: the member has read it (count and time are null).
   */
  is_read?: boolean;
  read_by_count?: number | null;
  seen_at?: string | null;
}

/**
 * The member's newest own message and whether the care team has read it, or
 * null when there is none or the server sent no read fields (older backends,
 * `comm:` threads) — better no caption than a wrong "Delivered".
 */
export function memberReadReceipt(
  messages: InboxMessage[],
): { id: number; seen: boolean; seenAt: string | null } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.direction !== 'outbound') continue;
    if (!('is_read' in m) && !('seen_at' in m) && !('read_by_count' in m)) return null;
    const seen = !!m.seen_at || m.is_read === true || (m.read_by_count ?? 0) > 0;
    return { id: m.id, seen, seenAt: m.seen_at || null };
  }
  return null;
}

export interface InboxThreadDetail {
  thread_key: string;
  source: 'comm' | 'channel';
  messages: InboxMessage[];
  /**
   * TRUS-709: an old `comm:` link resolves to the member DM — open this
   * thread instead ("channel:<id>"). Absent on threads that are current.
   */
  redirect_thread_key?: string | null;
}

export interface WorkflowAction {
  type: string;
  title: string;
  subtitle: string;
  action: string;
  /** Routing hint: "comm:<id>" (open inbox thread), "training", or "programs". */
  target: string;
}

export interface WorkflowActionsResponse {
  actions: WorkflowAction[];
  count: number;
}

async function inboxJson<T>(res: Response, fallback: string): Promise<T> {
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: fallback }));
    throw new Error(typeof error.detail === 'string' ? error.detail : fallback);
  }
  return res.json();
}

/** List all inbox threads for the member (across comm + channel sources). */
export async function getInboxThreads(): Promise<InboxThreadsResponse> {
  const res = await memberApiFetch('/inbox/threads');
  return inboxJson<InboxThreadsResponse>(res, 'Failed to load inbox');
}

/** Load a single thread's messages (auto-marks inbound messages read). */
export async function getInboxThread(threadKey: string): Promise<InboxThreadDetail> {
  const res = await memberApiFetch(`/inbox/threads/${encodeURIComponent(threadKey)}`);
  const detail = await inboxJson<InboxThreadDetail>(res, 'Failed to load conversation');
  // Tolerate a redirect-only body ({redirect_thread_key}) with no messages.
  return { ...detail, messages: Array.isArray(detail?.messages) ? detail.messages : [] };
}

/** A valid "channel:<id>" / "comm:<id>" thread key the inbox may open, else null. */
export function inboxRedirectKey(key: unknown, from: string): string | null {
  if (typeof key !== 'string') return null;
  const k = key.trim();
  return /^(channel|comm):\d{1,12}$/.test(k) && k !== from ? k : null;
}

/**
 * TRUS-709: who a message is from, as the member sees it. Staff messages carry
 * the replying staff member's name (or the organization's, when the org hides
 * staff names); an older server's bare "Staff" shows the organization instead.
 */
export function memberSenderLabel(msg: InboxMessage, orgName: string | null | undefined): string {
  const name = (msg.sender_name || '').trim();
  if (msg.direction === 'outbound' && (!name || name.toLowerCase() === 'staff')) {
    return orgName || name;
  }
  return name;
}

/**
 * TRUS-709: one thread per conversation. Drops `comm:` threads that point at a
 * DM (redirect_thread_key) when that DM is in the list, and any duplicate keys.
 */
export function dedupeInboxThreads(threads: InboxThread[]): InboxThread[] {
  const keys = new Set(threads.map((th) => th.thread_key));
  const seen = new Set<string>();
  return threads.filter((th) => {
    if (seen.has(th.thread_key)) return false;
    seen.add(th.thread_key);
    const target = inboxRedirectKey(th.redirect_thread_key, th.thread_key);
    return !(target && keys.has(target));
  });
}

export async function markInboxThreadRead(threadKey: string): Promise<void> {
  const res = await memberApiFetch(`/inbox/threads/${encodeURIComponent(threadKey)}/read`, {
    method: 'POST',
  });
  await inboxJson(res, 'Failed to mark read');
}

export async function markInboxThreadUnread(threadKey: string): Promise<void> {
  const res = await memberApiFetch(`/inbox/threads/${encodeURIComponent(threadKey)}/unread`, {
    method: 'POST',
  });
  await inboxJson(res, 'Failed to mark unread');
}

export async function setInboxThreadStar(threadKey: string, starred: boolean): Promise<void> {
  const res = await memberApiFetch(`/inbox/threads/${encodeURIComponent(threadKey)}/star`, {
    method: 'POST',
    body: JSON.stringify({ starred }),
  });
  await inboxJson(res, 'Failed to update star');
}

export async function setInboxThreadFlag(threadKey: string, flagged: boolean): Promise<void> {
  const res = await memberApiFetch(`/inbox/threads/${encodeURIComponent(threadKey)}/flag`, {
    method: 'POST',
    body: JSON.stringify({ flagged }),
  });
  await inboxJson(res, 'Failed to update flag');
}

/** Send a reply on a thread. Returns the new message id. */
export async function replyToInboxThread(
  threadKey: string,
  message: string,
): Promise<{ success: boolean; message_id: number }> {
  const res = await memberApiFetch(`/inbox/threads/${encodeURIComponent(threadKey)}/reply`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
  return inboxJson(res, 'Failed to send reply');
}

/** Actionable items for the member's home "Action needed" surface. */
export async function getWorkflowActions(): Promise<WorkflowActionsResponse> {
  const res = await memberApiFetch('/workflow-actions');
  return inboxJson<WorkflowActionsResponse>(res, 'Failed to load actions');
}

/**
 * Get stored member organizations
 */
export function getMemberOrganizations(): MemberOrganization[] {
  try {
    const data = localStorage.getItem('member_organizations');
    if (!data) return [];
    return JSON.parse(data);
  } catch {
    return [];
  }
}

/**
 * Fetch member organizations from API
 */
export async function fetchMemberOrganizations(): Promise<MemberOrganization[]> {
  const response = await authenticatedFetch('/organizations');

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load organizations' }));
    throw new Error(error.detail || 'Failed to load organizations');
  }

  const orgs = await response.json();
  localStorage.setItem('member_organizations', JSON.stringify(orgs));
  return orgs;
}

/**
 * Switch the member's active organization
 */
export async function switchMemberOrganization(organizationId: number): Promise<MemberLoginResponse> {
  const response = await authenticatedFetch('/switch-organization', {
    method: 'POST',
    body: JSON.stringify({ organization_id: organizationId }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to switch organization' }));
    throw new Error(error.detail || 'Failed to switch organization');
  }

  const result = await response.json();

  // Update localStorage with new token, member data, and the ACTIVE org so the
  // X-CBO-ID header (and thus the whole portal view) re-scopes to the picked org.
  if (result.token) {
    localStorage.setItem('member_token', result.token);
    localStorage.setItem('member_data', JSON.stringify(result.member));
    localStorage.setItem('active_cbo_id', String(organizationId));
  }

  return result;
}

/**
 * Get dashboard data for current member
 */
export async function getDashboard(): Promise<MemberDashboard> {
  const response = await authenticatedFetch('/dashboard');

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load dashboard' }));
    throw new Error(error.detail || 'Failed to load dashboard');
  }

  return response.json();
}

/**
 * Get communications for current member
 */
export async function getCommunications(unreadOnly: boolean = false): Promise<Communication[]> {
  const endpoint = unreadOnly ? '/communications?unread_only=true' : '/communications';
  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load communications' }));
    throw new Error(error.detail || 'Failed to load communications');
  }

  return response.json();
}

/**
 * Mark a communication as read
 * For workspace messages, pass source='workspace' and channelId
 */
export async function markCommunicationRead(
  commId: number,
  source?: string,
  channelId?: number
): Promise<void> {
  // Build query params for workspace messages
  const params = new URLSearchParams();
  if (source) params.append('source', source);
  if (channelId) params.append('channel_id', channelId.toString());

  const queryString = params.toString();
  const url = `/communications/${commId}/read${queryString ? `?${queryString}` : ''}`;

  const response = await authenticatedFetch(url, {
    method: 'POST',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to mark as read' }));
    throw new Error(error.detail || 'Failed to mark as read');
  }
}

/**
 * Respond to a communication
 */
export async function respondToCommunication(commId: number, message: string): Promise<void> {
  const response = await authenticatedFetch(`/communications/${commId}/respond`, {
    method: 'POST',
    body: JSON.stringify({
      communication_id: commId,
      response_message: message
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to send response' }));
    // Handle both string and array (validation error) formats
    let errorMessage = 'Failed to send response';
    if (typeof error.detail === 'string') {
      errorMessage = error.detail;
    } else if (Array.isArray(error.detail)) {
      errorMessage = error.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    throw new Error(errorMessage);
  }
}

/**
 * Send a message to a workspace channel
 */
export async function sendWorkspaceMessage(channelId: number, message: string): Promise<void> {
  const response = await authenticatedFetch(`/communications/workspace/${channelId}/send`, {
    method: 'POST',
    body: JSON.stringify({ message }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to send message' }));
    let errorMessage = 'Failed to send message';
    if (typeof error.detail === 'string') {
      errorMessage = error.detail;
    } else if (Array.isArray(error.detail)) {
      errorMessage = error.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
    }
    throw new Error(errorMessage);
  }
}

/**
 * Get referrals for current member
 */
export async function getReferrals(): Promise<any[]> {
  const response = await authenticatedFetch('/referrals');

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load referrals' }));
    throw new Error(error.detail || 'Failed to load referrals');
  }

  return response.json();
}

/**
 * Get enrollments for current member
 */
export async function getEnrollments(): Promise<any[]> {
  const response = await authenticatedFetch('/enrollments');

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load enrollments' }));
    throw new Error(error.detail || 'Failed to load enrollments');
  }

  return response.json();
}

/**
 * Get notes for current member
 */
export async function getNotes(): Promise<Note[]> {
  const response = await authenticatedFetch('/notes');

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load notes' }));
    throw new Error(error.detail || 'Failed to load notes');
  }

  return response.json();
}

/**
 * Create a new note
 */
export async function createNote(data: CreateNoteRequest): Promise<void> {
  const response = await authenticatedFetch('/notes', {
    method: 'POST',
    body: JSON.stringify({
      note_type: data.noteType,
      subject: data.subject,
      note_text: data.noteText,
      is_private: data.isPrivate,
      referral_id: data.referralId,
      enrollment_id: data.enrollmentId,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to create note' }));
    throw new Error(error.detail || 'Failed to create note');
  }
}

/**
 * Get documents for current member
 */
export async function getDocuments(): Promise<Document[]> {
  const response = await authenticatedFetch('/documents');

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load documents' }));
    throw new Error(error.detail || 'Failed to load documents');
  }

  return response.json();
}

/**
 * Download a document by ID — returns a blob URL for the browser
 */
export async function downloadDocument(documentId: number): Promise<string> {
  const token = getMemberToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE_URL}/documents/${documentId}/download`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error('Failed to download document');
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Delete a document by ID
 */
export async function deleteDocument(documentId: number): Promise<void> {
  const response = await authenticatedFetch(`/documents/${documentId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete document' }));
    throw new Error(error.detail || 'Failed to delete document');
  }
}

/**
 * Upload a document
 */
export async function uploadDocument(
  file: File,
  documentType: string,
  options: { description?: string; referralId?: number; enrollmentId?: number }
): Promise<void> {
  const token = getMemberToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);
  if (options.description) formData.append('description', options.description);
  if (options.referralId) formData.append('referral_id', options.referralId.toString());
  if (options.enrollmentId) formData.append('enrollment_id', options.enrollmentId.toString());

  const response = await fetch(`${API_BASE_URL}/documents/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to upload document' }));
    throw new Error(error.detail || 'Failed to upload document');
  }
}

/**
 * Get portal settings for current member
 */
export async function getSettings(): Promise<PortalSettings> {
  const response = await authenticatedFetch('/settings');

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load settings' }));
    throw new Error(error.detail || 'Failed to load settings');
  }

  return response.json();
}

/**
 * Update portal settings
 */
export async function updateSettings(settings: PortalSettings): Promise<void> {
  const response = await authenticatedFetch('/settings', {
    method: 'PUT',
    body: JSON.stringify({
      email_notifications_enabled: settings.emailNotificationsEnabled,
      sms_notifications_enabled: settings.smsNotificationsEnabled,
      preferred_contact_method: settings.preferredContactMethod,
      allow_portal_messages: settings.allowPortalMessages,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update settings' }));
    throw new Error(error.detail || 'Failed to update settings');
  }
}

/**
 * Get unified timeline of all communications across all channels
 */
export async function getUnifiedTimeline(
  options: {
    messageTypes?: string[];  // Filter by type: 'sms', 'email', 'portal', 'workspace'
    limit?: number;
    offset?: number;
  } = {}
): Promise<UnifiedTimeline> {
  const params = new URLSearchParams();
  if (options.messageTypes?.length) {
    params.append('message_types', options.messageTypes.join(','));
  }
  if (options.limit) params.append('limit', options.limit.toString());
  if (options.offset) params.append('offset', options.offset.toString());

  const queryString = params.toString();
  const endpoint = `/communications/unified-timeline${queryString ? `?${queryString}` : ''}`;

  const response = await authenticatedFetch(endpoint);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load timeline' }));
    throw new Error(error.detail || 'Failed to load timeline');
  }

  return response.json();
}

/**
 * Get communication channel summary for the member
 */
export async function getChannelSummary(): Promise<{
  channels: {
    type: string;
    display_name: string;
    last_message_at?: string;
    message_count: number;
    unread_count: number;
  }[];
  total_unread: number;
}> {
  const response = await authenticatedFetch('/communications/channels');

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to load channels' }));
    throw new Error(error.detail || 'Failed to load channels');
  }

  return response.json();
}
